import { isDefined } from "@clipboard-health/util-ts";

import { type TimecardFiles } from "../../../interface/shift";

/**
 * Detects and fixes swapped filename and fileStorageFileKey values in timesheet files.
 *
 * Format patterns:
 * - filename should be: <shift-id>.<extension> (simple format)
 * - fileStorageFileKey should be: timesheet/<shift-id>/<random>.<extension> (path format)
 *
 * @param file - The timecard file object to check and potentially fix
 * @returns The file object with corrected filename and fileStorageFileKey if swap was detected
 */
export function fixSwappedTimesheetKeys<
  FileType extends Pick<TimecardFiles, "filename" | "fileStorageFileKey">,
>(file: FileType): FileType {
  const { filename, fileStorageFileKey } = file;

  if (!isDefined(filename) || !isDefined(fileStorageFileKey)) {
    return file;
  }

  const filenameHasTimesheetPath = filename.startsWith("timesheet/");
  const fileStorageFileKeyIsSimple = !fileStorageFileKey.startsWith("timesheet/");

  if (filenameHasTimesheetPath && fileStorageFileKeyIsSimple) {
    return {
      ...file,
      filename: fileStorageFileKey,
      fileStorageFileKey: filename,
    };
  }

  return file;
}

/**
 * Applies the swap fix to an array of timecard files
 */
export function fixSwappedTimesheetKeysInArray<
  FileType extends Pick<TimecardFiles, "filename" | "fileStorageFileKey">,
>(files: FileType[]): FileType[] {
  return files.map((file) => ({
    ...fixSwappedTimesheetKeys(file),
  }));
}
